import { memo, useCallback, useState } from 'react';
import { Box, Button, Modal } from '@amzn/awsui-components-core';
import { useHistory } from 'react-router-dom';

import { EditIneligibilityReason } from '../../model/EditEligibility';
import styles from './EditForbiddenModal.module.scss';

let nextModalRootSeq = 0;

interface EditForbiddenModalProps {
  readonly reason: EditIneligibilityReason;
  readonly contentId: string;
  /** Alias of the lock holder, when `reason === 'lockedByOther'`. */
  readonly lockHolderAlias: string | undefined;
}

/** Modal title, keyed off the reason: a lock names the lock; every other block is a permission gate. */
function describeHeader(reason: EditIneligibilityReason): string {
  return reason === 'lockedByOther' ? 'Content locked' : "You can't edit this content";
}

/** Body copy explaining why editing is blocked, keyed off the reason. */
function describeReason(reason: EditIneligibilityReason, lockHolderAlias: string | undefined) {
  if (reason === 'lockedByOther') {
    return (
      <>
        This content is currently being edited by <strong>{lockHolderAlias}</strong>. You&apos;ll be
        taken to the read-only view until they finish.
      </>
    );
  }
  if (reason === 'role') {
    return 'Your current role cannot edit this content; make sure you selected the right role. You will be taken to the read-only view.';
  }
  if (reason === 'notOwner') {
    return 'Only the owner and co-authors can edit this content. You will be taken to the read-only view.';
  }
  return 'This content is in a stage that cannot be edited right now, such as an active publisher review. You will be taken to the read-only view.';
}

/**
 * Non-dismissible modal shown when the user opens the edit page for content
 * they may not edit; its only action routes to the read-only viewer.
 */
const EditForbiddenModal = memo(function EditForbiddenModal({
  reason,
  contentId,
  lockHolderAlias,
}: EditForbiddenModalProps) {
  const history = useHistory();

  // A per-instance portal root scopes the CSS rule that hides Cloudscape's
  // built-in dismiss (x) control, which has no prop to disable it. The lazy
  // initializer runs once, so the id is a stable non-empty string for this instance.
  const [modalRootId] = useState(() => `edit-forbidden-modal-root-${nextModalRootSeq++}`);

  const getModalRoot = useCallback(async () => {
    const className = styles.modalRoot ?? '';
    const existing = document.getElementById(modalRootId);
    if (existing) {
      existing.className = className;
      return existing;
    }
    const created = document.createElement('div');
    created.id = modalRootId;
    created.className = className;
    document.body.appendChild(created);
    return created;
  }, [modalRootId]);

  const removeModalRoot = useCallback(
    (modalRoot?: Element | null) => {
      if (modalRoot instanceof HTMLElement && modalRoot.id === modalRootId) {
        modalRoot.remove();
      }
    },
    [modalRootId],
  );

  // Replace (not push) so the browser Back button does not return to /edit.
  const goToViewer = useCallback(() => {
    history.replace(`/view/${contentId}`);
  }, [history, contentId]);

  return (
    // No onDismiss — the modal has a single deliberate exit to the viewer.
    <Modal
      visible
      getModalRoot={getModalRoot}
      removeModalRoot={removeModalRoot}
      header={describeHeader(reason)}
      footer={
        <Box float="right">
          <Button variant="primary" onClick={goToViewer}>
            Go to viewer
          </Button>
        </Box>
      }
    >
      {describeReason(reason, lockHolderAlias)}
    </Modal>
  );
});

export default EditForbiddenModal;
